import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L, { Map as LeafletMap, Marker as LeafletMarker } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './StoreLocatorEmbed.css';

// Dynamic store marker icon based on brands carried
const getStoreMarkerIcon = (store: { brands?: { red_bridge: boolean; tiny_dinosaur: boolean }; is_headquarters?: boolean }) => {
  const hasRB = store.brands?.red_bridge;
  const hasTD = store.brands?.tiny_dinosaur;
  const hasBoth = hasRB && hasTD;
  const hasNone = !hasRB && !hasTD;
  const isHQ = store.is_headquarters;

  // Headquarters gets a simple gold star marker (no logos)
  if (isHQ) {
    return L.divIcon({
      className: 'custom-store-marker',
      html: '<div class="headquarters-marker"><span class="hq-star">⭐</span></div>',
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
  }

  // Regular stores show brand logos
  let html = '<div class="store-marker">';
  if (hasRB) html += '<img src="/rbf-logo.png" class="marker-logo" alt="RBF" />';
  if (hasTD) html += '<img src="/tdf-logo.png" class="marker-logo" alt="TDF" />';
  if (hasNone) html += '<span class="marker-fallback">🏪</span>';
  html += '</div>';

  const width = hasBoth ? 52 : 30;
  const height = 30;

  return L.divIcon({
    className: 'custom-store-marker',
    html: html,
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height],
  });
};

// User location marker (blue dot style)
const userLocationIcon = L.divIcon({
  className: 'user-location-marker',
  html: '<div class="user-marker-dot"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Animal emoji map for compact display
const ANIMAL_EMOJI: Record<string, string> = {
  'Poultry - Layers': '🐔',
  'Poultry - Meat': '🍗',
  'Poultry': '🐔',
  'Goat': '🐐',
  'Sheep': '🐑',
  'Cattle': '🐄',
  'Swine': '🐷',
  'Rabbit': '🐰',
  'Horse': '🐴',
  'Dog': '🐕',
  'Cat': '🐱',
  'Game Bird': '🦃',
  'Turkey': '🦃',
  'Chicken': '🐔',
  'Pig': '🐷',
};

// API_BASE should NOT include /api - we add it in the fetch call
const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.redbridgefeed.com').replace(/\/api\/?$/, '');

interface StoreCategory {
  emoji: string;
  label: string;
}

interface AnimalType {
  key: string;
  label: string;
  emoji: string;
}

interface StoreBrands {
  red_bridge: boolean;
  tiny_dinosaur: boolean;
}

interface Store {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  hours: string;
  website: string;
  latitude: number;
  longitude: number;
  distance_miles: number | null;
  categories: StoreCategory[];
  brands: StoreBrands;
  animal_types: AnimalType[];
  is_headquarters?: boolean;
}

interface StoreLocatorResponse {
  stores: Store[];
  count: number;
  user_location: {
    lat: number | null;
    lng: number | null;
    from_zip: string | null;
  };
  available_filters: StoreCategory[];
}

// Component to recenter map when user location changes
function MapController({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

// Component to expose map instance to parent
function MapInstanceSetter({ mapRef }: { mapRef: React.MutableRefObject<LeafletMap | null> }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
}

// Kettle Falls, WA — default fallback location
const DEFAULT_LOCATION = { lat: 48.6114, lng: -117.8961 };

export default function StoreLocatorEmbed() {
  const isInIframe = useRef(window.self !== window.top);

  // Debug: confirm embed component mounts (not redirected away)
  useEffect(() => {
    console.log('[StoreLocatorEmbed] Component mounted, iframe:', isInIframe.current);
  }, []);

  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [zipCode, setZipCode] = useState('');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [locationStatus, setLocationStatus] = useState<'idle' | 'requesting' | 'denied'>('idle');
  const [showZipPrompt, setShowZipPrompt] = useState(false);

  // Refs for map interaction
  const storeListRef = useRef<HTMLDivElement>(null);
  const storeCardRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const markerRefs = useRef<{ [key: number]: LeafletMarker | null }>({});
  const mapRef = useRef<LeafletMap | null>(null);

  // Brand filter is controlled only by the dropdown (removed URL parameter filtering)
  const activeBrandFilter = selectedBrand;

  // Default center: Eastern Washington
  const defaultCenter: [number, number] = [47.5, -117.5];
  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultCenter);
  const [mapZoom, setMapZoom] = useState(7);

  const fetchStores = useCallback(async (lat?: number, lng?: number, zip?: string, brand?: string) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (lat && lng) {
        params.append('lat', lat.toString());
        params.append('lng', lng.toString());
      }
      if (zip) params.append('zip', zip);
      if (brand) params.append('brand', brand);

      const response = await fetch(`${API_BASE}/api/store-locator/?${params}`);
      if (!response.ok) throw new Error('Failed to fetch stores');

      const data: StoreLocatorResponse = await response.json();
      setStores(data.stores);

      // Center map on user location or first store
      if (data.user_location.lat && data.user_location.lng) {
        setUserLocation({ lat: data.user_location.lat, lng: data.user_location.lng });
        setMapCenter([data.user_location.lat, data.user_location.lng]);
        setMapZoom(9);
      } else if (data.stores.length > 0) {
        setMapCenter([data.stores[0].latitude, data.stores[0].longitude]);
        setMapZoom(8);
      }
    } catch (err) {
      setError('Unable to load stores. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Request user location on mount — skip geolocation entirely in iframes
  // (Shopify's Permissions-Policy blocks it, causing delays/errors)
  useEffect(() => {
    if (isInIframe.current) {
      // In iframe: use default location for initial sort, prompt for ZIP
      setLocationStatus('denied');
      setShowZipPrompt(true);
      fetchStores(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng, undefined, activeBrandFilter);
      return;
    }

    setLocationStatus('requesting');
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setLocationStatus('idle');
          fetchStores(latitude, longitude, undefined, activeBrandFilter);
        },
        () => {
          setLocationStatus('denied');
          setShowZipPrompt(true);
          fetchStores(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng, undefined, activeBrandFilter);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    } else {
      setLocationStatus('denied');
      setShowZipPrompt(true);
      fetchStores(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng, undefined, activeBrandFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when brand filter changes
  useEffect(() => {
    if (locationStatus !== 'requesting') {
      fetchStores(userLocation?.lat, userLocation?.lng, zipCode || undefined, activeBrandFilter || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandFilter]);

  const handleZipSearch = () => {
    if (zipCode.length >= 5) {
      setShowZipPrompt(false);
      fetchStores(undefined, undefined, zipCode, activeBrandFilter);
    }
  };

  const handleUseMyLocation = () => {
    if (isInIframe.current) {
      // Geolocation won't work in iframe — guide user to ZIP
      setShowZipPrompt(true);
      return;
    }
    setLocationStatus('requesting');
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setLocationStatus('idle');
          setShowZipPrompt(false);
          setZipCode('');
          fetchStores(latitude, longitude, undefined, activeBrandFilter);
        },
        () => {
          setLocationStatus('denied');
          setShowZipPrompt(true);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    }
  };

  const getDirectionsUrl = (store: Store) => {
    const address = `${store.address}, ${store.city}, ${store.state} ${store.zip}`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  const getCallUrl = (phone: string) => {
    return `tel:${phone.replace(/[^0-9+]/g, '')}`;
  };

  // Get emoji for an animal category
  const getAnimalEmoji = (label: string): string => {
    return ANIMAL_EMOJI[label] || '🐾';
  };

  // Fly to store and open popup after animation completes
  const flyToStoreAndOpenPopup = useCallback((store: Store) => {
    const map = mapRef.current;
    const marker = markerRefs.current[store.id];

    if (map) {
      map.flyTo([store.latitude, store.longitude], 14, {
        duration: 0.5,
        easeLinearity: 0.25
      });

      const openPopupAfterMove = () => {
        if (marker) {
          marker.openPopup();
        }
        map.off('moveend', openPopupAfterMove);
      };

      map.on('moveend', openPopupAfterMove);

      setTimeout(() => {
        map.off('moveend', openPopupAfterMove);
        if (marker) {
          marker.openPopup();
        }
      }, 600);
    } else if (marker) {
      marker.openPopup();
    }
  }, []);

  // Scroll to store card when marker is clicked
  const scrollToStoreCard = (store: Store) => {
    setSelectedStore(store);

    const cardElement = storeCardRefs.current[store.id];
    if (cardElement && storeListRef.current) {
      const listContainer = storeListRef.current;
      const cardTop = cardElement.offsetTop - listContainer.offsetTop;
      const offset = 16;

      listContainer.scrollTo({
        top: cardTop - offset,
        behavior: 'smooth'
      });
    }

    flyToStoreAndOpenPopup(store);
  };

  // Handle card click - fly map to store and open popup
  const handleCardClick = (store: Store) => {
    setSelectedStore(store);
    flyToStoreAndOpenPopup(store);
  };

  // Collect all unique animal emojis (max 6 displayed)
  const getStoreEmojis = (store: Store) => {
    const emojis: string[] = [];

    // From animal_types (manual checkboxes)
    store.animal_types?.forEach(animal => {
      if (animal.emoji && !emojis.includes(animal.emoji)) {
        emojis.push(animal.emoji);
      }
    });

    // From categories (order history)
    store.categories?.forEach(cat => {
      const emoji = getAnimalEmoji(cat.label);
      if (emoji && !emojis.includes(emoji)) {
        emojis.push(emoji);
      }
    });

    return emojis;
  };

  return (
    <div className="store-locator-embed">
      {/* Compact controls bar - single row */}
      <div className="embed-controls">
        {showZipPrompt && (
          <span className="zip-prompt-label">Enter your ZIP to find nearby stores</span>
        )}
        <input
          type="text"
          placeholder="ZIP code"
          className={`zip-input${showZipPrompt ? ' zip-input-highlight' : ''}`}
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onKeyPress={(e) => e.key === 'Enter' && handleZipSearch()}
          autoFocus={showZipPrompt}
        />

        {!isInIframe.current && (
          <button
            className="location-btn"
            onClick={handleUseMyLocation}
            disabled={locationStatus === 'requesting'}
            title="Use my location"
          >
            {locationStatus === 'requesting' ? '⏳' : '📍'}
          </button>
        )}

        <button
          className="search-btn"
          onClick={handleZipSearch}
          disabled={zipCode.length < 5}
        >
          Search
        </button>

        {/* Brand filter dropdown - always visible */}
        <select
          className="filter-select"
          value={selectedBrand}
          onChange={(e) => setSelectedBrand(e.target.value)}
        >
          <option value="">All Brands</option>
          <option value="redbridge">Red Bridge Feed</option>
          <option value="tinydino">Tiny Dinosaur Feed</option>
        </select>
      </div>

      <div className="embed-content">
        {/* Map (60% on desktop) */}
        <div className="embed-map">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapController center={mapCenter} zoom={mapZoom} />
            <MapInstanceSetter mapRef={mapRef} />

            {/* User location marker */}
            {userLocation && (
              <Marker
                position={[userLocation.lat, userLocation.lng]}
                icon={userLocationIcon}
              >
                <Popup>Your Location</Popup>
              </Marker>
            )}

            {/* Store markers with brand logos */}
            {stores.map((store) => (
              <Marker
                key={store.id}
                position={[store.latitude, store.longitude]}
                icon={getStoreMarkerIcon(store)}
                ref={(ref) => { markerRefs.current[store.id] = ref; }}
                eventHandlers={{
                  click: () => scrollToStoreCard(store),
                }}
              >
                <Popup>
                  <div className="map-popup">
                    <strong>{store.name}</strong>
                    <br />
                    {store.city}, {store.state}
                    {store.distance_miles && (
                      <>
                        <br />
                        <em>{store.distance_miles} miles</em>
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Store List (40% on desktop) */}
        <div className="embed-list" ref={storeListRef}>
          {loading ? (
            <div className="loading">Loading stores...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : stores.length === 0 ? (
            <div className="no-stores">No stores found nearby.</div>
          ) : (
            <>
              <p className="store-count">{stores.length} store{stores.length !== 1 ? 's' : ''} found</p>
              {stores.map((store) => {
                const emojis = getStoreEmojis(store);
                const directionsUrl = getDirectionsUrl(store);

                return (
                  <div
                    key={store.id}
                    ref={(el) => { storeCardRefs.current[store.id] = el; }}
                    className={`embed-store-card ${selectedStore?.id === store.id ? 'selected' : ''} ${store.is_headquarters ? 'is-hq' : ''}`}
                    onClick={() => handleCardClick(store)}
                  >
                    {/* Row 1: Store name + distance */}
                    <div className="card-row-1">
                      <h3 className="store-name">
                        {store.is_headquarters && <span className="hq-icon">⭐</span>}
                        {store.name}
                      </h3>
                      {store.distance_miles !== null && (
                        <span className="distance-badge">{store.distance_miles} mi</span>
                      )}
                    </div>

                    {/* HQ Badge (if applicable) */}
                    {store.is_headquarters && (
                      <div className="hq-badge-row">
                        <span className="hq-badge">HEADQUARTERS</span>
                      </div>
                    )}

                    {/* Row 2: Address */}
                    <p className="store-address">
                      {store.address}<br />
                      {store.city}, {store.state} {store.zip}
                    </p>

                    {/* Row 3: Animal emojis (compact) */}
                    {emojis.length > 0 && (
                      <div className="animal-emojis">
                        {emojis.slice(0, 6).map((emoji, idx) => (
                          <span key={idx} className="emoji-badge">{emoji}</span>
                        ))}
                        {emojis.length > 6 && (
                          <span className="emoji-more">+{emojis.length - 6}</span>
                        )}
                      </div>
                    )}

                    {/* Row 4: Action buttons */}
                    <div className="card-actions">
                      <a
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>📍</span> Directions
                      </a>
                      {store.phone && (
                        <a
                          href={getCallUrl(store.phone)}
                          className="action-btn icon-only"
                          onClick={(e) => e.stopPropagation()}
                          title="Call store"
                        >
                          📞
                        </a>
                      )}
                      {store.website && (
                        <a
                          href={store.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn icon-only"
                          onClick={(e) => e.stopPropagation()}
                          title="Visit website"
                        >
                          🌐
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
